import { Position } from '@turf/turf';
import {
  CoverageGrid,
  NO_INDEX,
  isFilledCell,
} from 'app/utils/field-boundary/grid';
import { MIN_RING_VERTICES } from 'app/utils/field-boundary/shared';

interface OutlineEdges {
  /** For each grid vertex, the vertex its outgoing outline edge leads to. */
  nextVertex: Int32Array;
  start: number;
  edgeCount: number;
}

/**
 * Links the outline edges of the filled cells counterclockwise (filled side
 * on the left). Vertices are numbered row by row over a (columns + 1) x
 * (rows + 1) lattice.
 */
function linkOutlineEdges(grid: CoverageGrid): OutlineEdges {
  const vertexColumns = grid.columns + 1;
  const nextVertex = new Int32Array(vertexColumns * (grid.rows + 1)).fill(
    NO_INDEX,
  );
  let start = NO_INDEX;
  let edgeCount = 0;

  for (let row = 0; row < grid.rows; row++) {
    for (let column = 0; column < grid.columns; column++) {
      if (isFilledCell(grid, column, row)) {
        const bottomLeft = row * vertexColumns + column;
        const bottomRight = bottomLeft + 1;
        const topLeft = bottomLeft + vertexColumns;
        const topRight = topLeft + 1;
        if (!isFilledCell(grid, column, row - 1)) {
          nextVertex[bottomLeft] = bottomRight;
          edgeCount++;
        }
        if (!isFilledCell(grid, column + 1, row)) {
          nextVertex[bottomRight] = topRight;
          edgeCount++;
        }
        if (!isFilledCell(grid, column, row + 1)) {
          nextVertex[topRight] = topLeft;
          edgeCount++;
        }
        if (!isFilledCell(grid, column - 1, row)) {
          nextVertex[topLeft] = bottomLeft;
          edgeCount++;
        }
        if (start === NO_INDEX) {
          // The first filled cell in scan order has empty cells below and to
          // its left, so its bottom-left vertex is always a corner.
          start = bottomLeft;
        }
      }
    }
  }
  return { nextVertex, start, edgeCount };
}

/**
 * Traces the outline of the filled cells counterclockwise, in grid units,
 * with one vertex per unit edge. The ring is left open.
 */
export function traceOuterRing(grid: CoverageGrid): Position[] | null {
  const { nextVertex, start, edgeCount } = linkOutlineEdges(grid);
  if (start === NO_INDEX) {
    return null;
  }

  const vertexColumns = grid.columns + 1;
  const toGridPosition = (vertex: number): Position => [
    vertex % vertexColumns,
    Math.floor(vertex / vertexColumns),
  ];
  const ring: Position[] = [toGridPosition(start)];
  let current = nextVertex[start];
  let steps = 1;
  while (current !== start && current !== NO_INDEX && steps <= edgeCount) {
    ring.push(toGridPosition(current));
    current = nextVertex[current];
    steps++;
  }

  if (current !== start || ring.length < MIN_RING_VERTICES) {
    return null;
  }
  return ring;
}

/**
 * Replaces the staircase outline of the raster by the midpoints of its unit
 * edges (as marching squares would), so that steps along a slanted or curved
 * edge become straight segments that the simplification can then collapse.
 */
export function smoothStaircase(ring: Position[]): Position[] {
  const smoothed = ring.map(([x, y], index) => {
    const [nextX, nextY] = ring[(index + 1) % ring.length];
    return [(x + nextX) / 2, (y + nextY) / 2];
  });
  smoothed.push(smoothed[0]);
  return smoothed;
}
